/**
 * The three form steps, the thanks screen, and what sits below the fold.
 * Pure presentation: state and navigation live in App.tsx.
 */
import {
  AGENT_ACCESS,
  AGENT_CHANNELS,
  AGENT_TASKS,
  BLOG_WRITERS,
  CATEGORIES,
  CHANNELS,
  CONSENT_TEXT,
  EVENT_TYPES,
  GUEST_COUNTS,
  HAS_DOMAIN,
  HEARD_FROM,
  LOGO_ADDONS,
  LOGO_LAYERED_TEXT,
  LOGO_NEEDS,
  LOGO_PAGE,
  LOGO_PORTFOLIO_TEXT,
  LOGO_RULES,
  LOGO_SCOPE_TEXT,
  LOGO_STYLES,
  LOGO_USES,
  OPTIMIZE_GOALS,
  PERSONAL_TYPES,
  RSVP_ADDRESSES,
  RSVP_FEATURES,
  SITE_ACCESS,
  SITE_PLATFORMS,
  TIMELINES,
  USER_COUNTS,
  WEB_APP_CURRENT,
  WEB_APP_FEATURES,
  WEB_APP_USERS,
  FAQ,
  HOW,
  budgetsFor,
  formatPesos,
  labelOf,
  logoNeedsCurrent,
  logoNeedsScope,
  type Category,
  type Channel,
} from '@codasc/studio-shared';
import { type ReactNode, useEffect, useState } from 'react';
import {
  ASKS_ORGANIZATION,
  type Errors,
  type FormState,
  MIN_DESCRIPTION,
  needsOrganization,
} from './form';
import { LOGO_STYLE_ICONS } from './logoStyles';
import { Choice, Field, Multi, Tick } from './ui';

export const MESSENGER_URL = 'https://m.me/CodascSoftwarePublishing';
/**
 * The header's "Message us" menu. The first two are Codasc short links, so
 * where they point can change without a release; the address is the same one
 * the landing page gives.
 */
export const CONTACT_EMAIL = 'hello@codasc.dev';
export const CONTACT_LINKS = [
  { id: 'messenger', label: 'Facebook Messenger', href: 'https://codasc.dev/s/me' },
  { id: 'instagram', label: 'Instagram', href: 'https://codasc.dev/s/ig' },
  { id: 'email', label: CONTACT_EMAIL, href: `mailto:${CONTACT_EMAIL}` },
] as const;
export type ContactLinkId = (typeof CONTACT_LINKS)[number]['id'];
export const SITE_URL = 'https://codasc.dev';
export const LEGAL = `${SITE_URL}/legal`;
/** `/studio/logo`. The draft is in sessionStorage, so leaving for it loses nothing. */
export const LOGO_PAGE_URL = `${import.meta.env.BASE_URL}${LOGO_PAGE.path}`;
/** The Codasc Creatives deck: logos we have made, and how they move. A short link, so the deck can move. */
export const LOGO_PORTFOLIO_URL = 'https://codasc.dev/s/creatives-deck';

type Patch = (p: Partial<FormState>) => void;

/**
 * Everything that isn't required, behind one tap.
 *
 * The full step-2 form is a dozen fields; shown at once it reads as work, and
 * ad traffic leaves. The required few are what a quote actually needs — the
 * rest sharpen it, so they stay one tap away rather than gone.
 */
function More({ count, children }: { count: number; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  if (open) return <div className="st-more-open">{children}</div>;
  return (
    <button type="button" className="st-more" onClick={() => setOpen(true)}>
      <span>Add more details</span>
      <span className="st-more-n">{count} optional · helps us quote</span>
    </button>
  );
}

// ─── Step 1 ──────────────────────────────────────────────────────────────

export function StepCategory({
  state,
  patch,
  onPick,
  errors,
}: {
  state: FormState;
  patch: Patch;
  onPick: (c: Category) => void;
  errors: Errors;
}) {
  const [customOpen, setCustomOpen] = useState(state.category === 'custom');

  return (
    <section className="st-hero" aria-labelledby="st-h1">
      <p className="st-eyebrow">Codasc Studio</p>
      <h1 id="st-h1">
        Let's build your <em>app or website.</em>
      </h1>
      <p className="st-dek">
        Pick what you need, tell us about it, and we'll get back to you with a quote as soon as we
        can. No commitment.
      </p>

      <div className="st-cards" role="list">
        {CATEGORIES.map((c) => {
          const selected = state.category === c.id;
          return (
            <button
              key={c.id}
              type="button"
              role="listitem"
              className={`st-card${selected ? ' is-on' : ''}`}
              aria-pressed={selected}
              onClick={() => {
                if (c.id === 'custom') {
                  patch({ category: 'custom' });
                  setCustomOpen(true);
                  return;
                }
                setCustomOpen(false);
                onPick(c.id);
              }}
            >
              <span className="st-card-top">
                <span className="st-card-title">{c.label}</span>
                <span className="st-card-arrow" aria-hidden="true">
                  →
                </span>
              </span>
              <span className="st-card-blurb">{c.blurb}</span>
              <span className="st-card-price">
                {c.fromCentavos === null
                  ? 'Custom quote'
                  : `Starts at ${formatPesos(c.fromCentavos)}`}
                {c.monthlyFromCentavos ? (
                  <span className="st-card-monthly">
                    {' '}
                    + {formatPesos(c.monthlyFromCentavos)}/month to run it
                  </span>
                ) : null}
              </span>
            </button>
          );
        })}
      </div>

      {customOpen ? (
        <div className="st-custom">
          <Field label="What do you have in mind?" htmlFor="st-other" error={errors.categoryOther}>
            <input
              id="st-other"
              className="st-input"
              value={state.categoryOther}
              maxLength={200}
              autoFocus
              placeholder="e.g. an online store, a mobile game, an internal tool"
              onChange={(e) => patch({ categoryOther: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && state.categoryOther.trim()) onPick('custom');
              }}
            />
          </Field>
          <button
            type="button"
            className="st-btn st-btn-primary"
            disabled={!state.categoryOther.trim()}
            onClick={() => onPick('custom')}
          >
            Continue
          </button>
        </div>
      ) : null}

      <p className="st-fine">
        Final price after we review your details. Logo work has its own rules:{' '}
        <a href={LOGO_PAGE_URL}>see how it goes</a>.
      </p>
    </section>
  );
}

/**
 * The column beside the form on a wide screen.
 *
 * Steps 2 and 3 are one narrow column, so on a desktop the right half of the
 * page was empty. What fills it is the answer to "what am I signing up for",
 * which until now only existed on step 1 — the screen the visitor has left.
 *
 * It is deliberately NOT a progress tracker. The form already has one (the
 * bar and "Step 2 of 3"), and a second list with numbers and ticks beside it
 * read as a competing, wrong one. So this starts where the form ends: only
 * what happens after they press send, unnumbered.
 */
export function StepAside({ step }: { step: 2 | 3 }) {
  return (
    <aside className="st-aside" aria-label="After you send this">
      <h2 className="st-aside-h">After you send this</h2>
      <dl className="st-aside-list">
        {HOW.slice(2).map(([title, body]) => (
          <div key={title}>
            <dt>{title}</dt>
            <dd>{body}</dd>
          </div>
        ))}
      </dl>
      <p className="st-aside-note">
        {step === 2
          ? 'Free to ask. Nothing is committed until you approve a quote.'
          : 'We only use your details to reply to this request.'}
      </p>
    </aside>
  );
}

// ─── Step 2 ──────────────────────────────────────────────────────────────

const DETAIL_HEADINGS: Record<Category, string> = {
  web_app: 'Tell us about your app',
  web_opt: 'Tell us about your site',
  ai_agent: 'Tell us about your agent',
  rsvp: 'Tell us about your event',
  personal: 'Tell us about your site',
  logo: 'Tell us about your logo',
  custom: 'Tell us more',
};

/** How many optional fields sit behind "Add more details". 0 hides it. */
const OPTIONAL_COUNT: Record<Category, number> = {
  web_app: 6,
  web_opt: 3,
  ai_agent: 5,
  rsvp: 5,
  personal: 5,
  logo: 4,
  custom: 2,
};

const DESCRIPTION_PROMPTS: Record<Category, { label: string; placeholder: string }> = {
  ai_agent: {
    label: 'What should the agent take off your hands?',
    placeholder:
      'e.g. Watch our sales inbox, answer the routine questions from our price list, and flag anything unusual to me on Telegram.',
  },
  web_app: {
    label: 'What should the app do, and for who?',
    placeholder:
      'e.g. Patients book a slot and pay a deposit with GCash; my staff see the day’s schedule.',
  },
  rsvp: {
    label: 'Tell us about the event',
    placeholder:
      'e.g. Our wedding in Legazpi — we want the invite, RSVP with meal choice, and a story of how we met.',
  },
  personal: {
    label: 'What is the site for?',
    placeholder: 'e.g. A portfolio of my wedding photography, with an inquiry form for bookings.',
  },
  web_opt: {
    label: 'What would you like fixed or improved?',
    placeholder:
      'e.g. It takes forever to load on mobile, and the booking form stopped sending emails last month.',
  },
  logo: {
    label: 'Tell us about your business, and anything else you want to ask',
    placeholder:
      'e.g. A milk tea shop in Naga. Our customers are students, and most orders come through Facebook. Can you also do a version for our cups?',
  },
  custom: {
    label: 'Tell us about it',
    placeholder: 'What it should do, who uses it, and anything you’ve already tried.',
  },
};

/**
 * The logo rules, on the page where the visitor is deciding.
 *
 * "What you get" is open, because it is the offer. The rest is one tap away:
 * scope, revisions, approval, add-ons and ownership are what a client argues
 * about later, so they have to be readable before anyone sends a request —
 * but not stacked above the form on a phone.
 */
function LogoRules() {
  const [first, ...rest] = LOGO_RULES;
  return (
    <section className="st-rules" aria-labelledby="st-rules-h">
      <h3 id="st-rules-h" className="st-rules-h">
        How logo work goes
      </h3>
      {first ? (
        <div className="st-rules-sec">
          <p className="st-rules-t">{first[0]}</p>
          <ul>
            {first[1].map((point) => (
              <li key={point}>{point}</li>
            ))}
          </ul>
        </div>
      ) : null}
      <details className="st-rules-more">
        <summary>Read the rules: scope, revisions, approval, add-ons</summary>
        {rest.map(([title, points]) => (
          <div key={title} className="st-rules-sec">
            <p className="st-rules-t">{title}</p>
            <ul>
              {points.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          </div>
        ))}
      </details>
      <p className="st-rules-link">
        <a href={LOGO_PAGE_URL}>See example styles and every rule</a>
      </p>
      <p className="st-rules-link">
        <a href={LOGO_PORTFOLIO_URL} target="_blank" rel="noopener noreferrer">
          See logos we’ve made
        </a>
      </p>
    </section>
  );
}

export function StepDetails({
  state,
  patch,
  errors,
}: {
  state: FormState;
  patch: Patch;
  errors: Errors;
}) {
  const category = state.category ?? 'custom';
  const prompt = DESCRIPTION_PROMPTS[category];
  const optionalCount = OPTIONAL_COUNT[category];

  return (
    <section aria-labelledby="st-h2">
      <h2 id="st-h2" className="st-step-h">
        {DETAIL_HEADINGS[category]}
      </h2>

      {category === 'rsvp' ? (
        <>
          <Field
            label="What kind of event?"
            field="details.eventType"
            error={errors['details.eventType']}
          >
            <Choice
              label="Event type"
              options={EVENT_TYPES}
              value={state.rsvp.eventType}
              onChange={(eventType) => patch({ rsvp: { ...state.rsvp, eventType } })}
            />
          </Field>
          <Field label="Event date" htmlFor="st-date" field="eventDate" error={errors.eventDate}>
            <input
              id="st-date"
              type="date"
              className="st-input st-input-date"
              value={state.eventDate}
              onChange={(e) => patch({ eventDate: e.target.value })}
            />
          </Field>
        </>
      ) : null}

      {category === 'ai_agent' ? (
        <>
          <Field
            label="Where should people talk to it?"
            hint="Pick any that apply."
            field="details.channels"
            error={errors['details.channels']}
          >
            <Multi
              label="Channels"
              options={AGENT_CHANNELS}
              value={state.agent.channels}
              onChange={(channels) => patch({ agent: { ...state.agent, channels } })}
            />
          </Field>
          {state.agent.channels.includes('other') ? (
            <Field
              label="Which app?"
              htmlFor="st-chan-other"
              field="details.channelOther"
              error={errors['details.channelOther']}
            >
              <input
                id="st-chan-other"
                className="st-input"
                maxLength={200}
                value={state.agent.channelOther}
                placeholder="e.g. Viber, Messenger, Microsoft Teams"
                onChange={(e) => patch({ agent: { ...state.agent, channelOther: e.target.value } })}
              />
            </Field>
          ) : null}
        </>
      ) : null}

      {category === 'personal' ? (
        <Field
          label="What kind of site?"
          field="details.siteType"
          error={errors['details.siteType']}
        >
          <Choice
            label="Site type"
            options={PERSONAL_TYPES}
            value={state.personal.siteType}
            onChange={(siteType) => patch({ personal: { ...state.personal, siteType } })}
          />
        </Field>
      ) : null}

      {category === 'web_opt' ? (
        <>
          {/* First, and required: everything else here is a question about a
              thing we have not seen yet. */}
          <Field
            label="Where can we see it?"
            htmlFor="st-site"
            hint="Paste the link. If it isn’t public, say where it lives."
            field="details.siteUrl"
            error={errors['details.siteUrl']}
          >
            <input
              id="st-site"
              className="st-input"
              inputMode="url"
              maxLength={300}
              value={state.webOpt.siteUrl}
              placeholder="https://"
              onChange={(e) => patch({ webOpt: { ...state.webOpt, siteUrl: e.target.value } })}
            />
          </Field>
          <Field
            label="What should we work on?"
            hint="Pick any that apply."
            field="details.goals"
            error={errors['details.goals']}
          >
            <Multi
              label="What to improve"
              options={OPTIMIZE_GOALS}
              value={state.webOpt.goals}
              onChange={(goals) => patch({ webOpt: { ...state.webOpt, goals } })}
            />
          </Field>
        </>
      ) : null}

      {category === 'logo' ? (
        <>
          <LogoRules />
          <Field label="What do you need?" field="details.need" error={errors['details.need']}>
            <Choice
              label="What you need"
              options={LOGO_NEEDS}
              value={state.logo.need}
              onChange={(need) => patch({ logo: { ...state.logo, need } })}
            />
          </Field>
          {state.logo.need && logoNeedsCurrent(state.logo.need) ? (
            <Field
              label="Where can we see your logo?"
              htmlFor="st-logo-url"
              hint="A link to your Facebook page, website, or a shared Drive file. You can send the file itself after we reply."
              field="details.currentLogoUrl"
              error={errors['details.currentLogoUrl']}
            >
              <input
                id="st-logo-url"
                className="st-input"
                inputMode="url"
                maxLength={300}
                value={state.logo.currentLogoUrl}
                placeholder="https://"
                onChange={(e) => patch({ logo: { ...state.logo, currentLogoUrl: e.target.value } })}
              />
            </Field>
          ) : null}
          {state.logo.need === 'new' ? (
            <Field label="What kind of logo?" hint="Pick any you like." optional>
              <Multi
                label="Logo style"
                options={LOGO_STYLES}
                icons={LOGO_STYLE_ICONS}
                value={state.logo.styles}
                onChange={(styles) => patch({ logo: { ...state.logo, styles } })}
              />
            </Field>
          ) : null}
        </>
      ) : null}

      <Field label={prompt.label} htmlFor="st-desc" field="description" error={errors.description}>
        <textarea
          id="st-desc"
          className="st-input st-textarea"
          rows={4}
          maxLength={4000}
          value={state.description}
          placeholder={prompt.placeholder}
          onChange={(e) => patch({ description: e.target.value })}
        />
        {state.description.trim().length > 0 &&
        state.description.trim().length < MIN_DESCRIPTION ? (
          <p className="st-hint">A little more, please.</p>
        ) : null}
      </Field>

      {category !== 'rsvp' ? (
        <Field label="When do you need it?" field="timeline" error={errors.timeline}>
          <Choice
            label="Timeline"
            options={TIMELINES}
            value={state.timeline}
            onChange={(timeline) => patch({ timeline })}
          />
        </Field>
      ) : null}

      <Field label="Budget" field="budget" error={errors.budget}>
        <Choice
          label="Budget"
          options={budgetsFor(category)}
          value={state.budget}
          onChange={(budget) => patch({ budget })}
        />
      </Field>

      {category === 'logo' && state.logo.need && logoNeedsScope(state.logo.need) ? (
        <Tick
          field="details.scopeVersion"
          checked={state.logo.scope}
          onChange={(scope) => patch({ logo: { ...state.logo, scope } })}
          error={errors['details.scopeVersion']}
        >
          {LOGO_SCOPE_TEXT}
        </Tick>
      ) : null}
      {category === 'logo' && state.logo.need === 'animate' ? (
        <Tick
          field="details.layeredSvg"
          checked={state.logo.layeredSvg}
          onChange={(layeredSvg) => patch({ logo: { ...state.logo, layeredSvg } })}
          error={errors['details.layeredSvg']}
        >
          {LOGO_LAYERED_TEXT}
        </Tick>
      ) : null}
      {category === 'logo' && state.logo.need ? (
        <Tick
          field="details.portfolio"
          checked={state.logo.portfolio}
          onChange={(portfolio) => patch({ logo: { ...state.logo, portfolio } })}
        >
          {LOGO_PORTFOLIO_TEXT} Optional.
        </Tick>
      ) : null}

      <More count={optionalCount}>
        {category === 'web_app' ? (
          <>
            <Field label="Who will use it?" optional>
              <Multi
                label="Users"
                options={WEB_APP_USERS}
                value={state.webApp.users}
                onChange={(users) => patch({ webApp: { ...state.webApp, users } })}
              />
            </Field>
            <Field label="What should it have?" hint="Pick any that apply." optional>
              <Multi
                label="Features"
                options={WEB_APP_FEATURES}
                value={state.webApp.features}
                onChange={(features) => patch({ webApp: { ...state.webApp, features } })}
              />
            </Field>
            <Field label="What do you use for this now?" optional>
              <Choice
                label="Current tool"
                options={WEB_APP_CURRENT}
                value={state.webApp.current}
                onChange={(current) => patch({ webApp: { ...state.webApp, current } })}
              />
            </Field>
            <Field label="About how many users?" optional>
              <Choice
                label="User count"
                options={USER_COUNTS}
                value={state.webApp.userCount}
                onChange={(userCount) => patch({ webApp: { ...state.webApp, userCount } })}
              />
            </Field>
          </>
        ) : null}

        {category === 'web_opt' ? (
          <>
            <Field label="What is it built with?" optional>
              <Choice
                label="Platform"
                options={SITE_PLATFORMS}
                value={state.webOpt.platform}
                onChange={(platform) => patch({ webOpt: { ...state.webOpt, platform } })}
              />
            </Field>
            <Field
              label="Do you have the logins for it?"
              hint="The admin account, the hosting, or whoever built it."
              optional
            >
              <Choice
                label="Has the logins"
                options={SITE_ACCESS}
                value={state.webOpt.access}
                onChange={(access) => patch({ webOpt: { ...state.webOpt, access } })}
              />
            </Field>
          </>
        ) : null}

        {category === 'ai_agent' ? (
          <>
            <Field label="What should it do?" hint="Pick any that apply." optional>
              <Multi
                label="Tasks"
                options={AGENT_TASKS}
                value={state.agent.tasks}
                onChange={(tasks) => patch({ agent: { ...state.agent, tasks } })}
              />
            </Field>
            <Field label="Who will use it?" optional>
              <Choice
                label="Who will use it"
                options={AGENT_ACCESS}
                value={state.agent.access}
                onChange={(access) => patch({ agent: { ...state.agent, access } })}
              />
            </Field>
            <Field
              label="What should it be able to reach?"
              htmlFor="st-connects"
              hint="The tools it needs access to."
              optional
            >
              <input
                id="st-connects"
                className="st-input"
                maxLength={500}
                value={state.agent.connects}
                placeholder="e.g. Gmail, Google Drive, our booking sheet"
                onChange={(e) => patch({ agent: { ...state.agent, connects: e.target.value } })}
              />
            </Field>
          </>
        ) : null}

        {category === 'rsvp' ? (
          <>
            <Field label="How many guests?" optional>
              <Choice
                label="Guest count"
                options={GUEST_COUNTS}
                value={state.rsvp.guestCount}
                onChange={(guestCount) => patch({ rsvp: { ...state.rsvp, guestCount } })}
              />
            </Field>
            <Field label="What would you like?" hint="Pick any that apply." optional>
              <Multi
                label="Features"
                options={RSVP_FEATURES}
                value={state.rsvp.features}
                onChange={(features) => patch({ rsvp: { ...state.rsvp, features } })}
              />
            </Field>
            <Field label="Web address" optional>
              <Choice
                label="Web address"
                options={RSVP_ADDRESSES}
                value={state.rsvp.address}
                onChange={(address) => patch({ rsvp: { ...state.rsvp, address } })}
              />
            </Field>
          </>
        ) : null}

        {category === 'personal' ? (
          <>
            <Field label="What do you do?" htmlFor="st-prof" optional>
              <input
                id="st-prof"
                className="st-input"
                maxLength={120}
                value={state.personal.profession}
                placeholder="e.g. Wedding photographer, freelance VA, dentist"
                onChange={(e) =>
                  patch({ personal: { ...state.personal, profession: e.target.value } })
                }
              />
            </Field>
            <Field
              label="Moving an existing site?"
              htmlFor="st-migr"
              hint="Paste its link, if any."
              optional
            >
              <input
                id="st-migr"
                className="st-input"
                maxLength={300}
                inputMode="url"
                value={state.personal.migrateFrom}
                placeholder="https://"
                onChange={(e) =>
                  patch({ personal: { ...state.personal, migrateFrom: e.target.value } })
                }
              />
            </Field>
            {state.personal.siteType === 'blog' ? (
              <Field label="Who writes the posts?" optional>
                <Choice
                  label="Who writes the posts"
                  options={BLOG_WRITERS}
                  value={state.personal.blogWriter}
                  onChange={(blogWriter) => patch({ personal: { ...state.personal, blogWriter } })}
                />
              </Field>
            ) : null}
          </>
        ) : null}

        {category === 'logo' ? (
          <>
            <Field label="Where will you use it?" hint="Pick any that apply." optional>
              <Multi
                label="Where it is used"
                options={LOGO_USES}
                value={state.logo.uses}
                onChange={(uses) => patch({ logo: { ...state.logo, uses } })}
              />
            </Field>
            {state.logo.need !== 'animate' ? (
              <Field label="Colors you want, or want to avoid" htmlFor="st-colors" optional>
                <input
                  id="st-colors"
                  className="st-input"
                  maxLength={300}
                  value={state.logo.colors}
                  placeholder="e.g. Navy and gold, no red"
                  onChange={(e) => patch({ logo: { ...state.logo, colors: e.target.value } })}
                />
              </Field>
            ) : null}
            <Field label="Add-ons" hint="Priced on top of the logo." optional>
              <Multi
                label="Add-ons"
                options={LOGO_ADDONS}
                value={state.logo.addons}
                onChange={(addons) => patch({ logo: { ...state.logo, addons } })}
              />
            </Field>
          </>
        ) : null}

        <Field
          label={category === 'logo' ? 'Logos you like' : 'Sites or apps you like'}
          htmlFor="st-refs"
          hint="Links help us see what you have in mind."
          optional
        >
          <input
            id="st-refs"
            className="st-input"
            inputMode="url"
            maxLength={1500}
            value={state.references}
            placeholder="https://"
            onChange={(e) => patch({ references: e.target.value })}
          />
        </Field>

        {/* Nothing to ask someone whose site is already at an address, or who
            is not having a site built at all. */}
        {category !== 'web_opt' && category !== 'logo' ? (
          <Field label="Do you have a domain (web address)?" optional>
            <Choice
              label="Has a domain"
              options={HAS_DOMAIN}
              value={state.hasDomain}
              onChange={(hasDomain) => patch({ hasDomain })}
            />
          </Field>
        ) : null}
      </More>
    </section>
  );
}

export function StepContact({
  state,
  patch,
  errors,
  askHeardFrom,
}: {
  state: FormState;
  patch: Patch;
  errors: Errors;
  askHeardFrom: boolean;
}) {
  const channel = state.channel;
  const mobileFirst = channel !== 'email';

  const mobile = (
    <Field
      key="mobile"
      label="Mobile number"
      htmlFor="st-mobile"
      field="mobile"
      error={errors.mobile}
      optional={channel === 'email'}
    >
      <input
        id="st-mobile"
        className="st-input"
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        maxLength={40}
        value={state.mobile}
        placeholder="0917 123 4567"
        onChange={(e) => patch({ mobile: e.target.value })}
      />
    </Field>
  );
  const email = (
    <Field
      key="email"
      label="Email"
      htmlFor="st-email"
      field="email"
      error={errors.email}
      optional={channel !== 'email'}
    >
      <input
        id="st-email"
        className="st-input"
        type="email"
        inputMode="email"
        autoComplete="email"
        maxLength={200}
        value={state.email}
        placeholder="you@example.com"
        onChange={(e) => patch({ email: e.target.value })}
      />
    </Field>
  );

  return (
    <section aria-labelledby="st-h3">
      <h2 id="st-h3" className="st-step-h">
        How do we reach you?
      </h2>

      <Field label="Your name" htmlFor="st-name" field="name" error={errors.name}>
        <input
          id="st-name"
          className="st-input"
          autoComplete="name"
          maxLength={120}
          value={state.name}
          onChange={(e) => patch({ name: e.target.value })}
        />
      </Field>

      <Field label="Best way to reach you" field="channel" error={errors.channel}>
        <Choice
          label="Channel"
          options={CHANNELS}
          value={channel}
          onChange={(c) => patch({ channel: c })}
        />
      </Field>
      {channel === 'messenger' ? (
        <p className="st-note">
          Facebook doesn't let a Page message you first, so after you send this, tap “Message us”
          and we'll reply there. Leave a number or email too, in case it doesn't connect.
        </p>
      ) : null}

      {mobileFirst ? [mobile, email] : [email, mobile]}

      {state.category && ASKS_ORGANIZATION.includes(state.category) ? (
        <Field
          label={
            needsOrganization(state.category)
              ? 'Business name, as it should appear on the logo'
              : 'Business or organization'
          }
          htmlFor="st-org"
          field="organization"
          error={errors.organization}
          optional={!needsOrganization(state.category)}
        >
          <input
            id="st-org"
            className="st-input"
            autoComplete="organization"
            maxLength={160}
            value={state.organization}
            onChange={(e) => patch({ organization: e.target.value })}
          />
        </Field>
      ) : null}

      {askHeardFrom ? (
        <Field label="How did you find us?" optional>
          <Choice
            label="How did you find us"
            options={HEARD_FROM}
            value={state.heardFrom}
            onChange={(heardFrom) => patch({ heardFrom })}
          />
        </Field>
      ) : null}

      {/* Honeypot: off-screen, out of the tab order, ignored by autofill. */}
      <div className="st-hp" aria-hidden="true">
        <label htmlFor="st-website">Website</label>
        <input
          id="st-website"
          tabIndex={-1}
          autoComplete="off"
          value={state.website}
          onChange={(e) => patch({ website: e.target.value })}
        />
      </div>

      <div className={`st-consent${errors.consent ? ' has-err' : ''}`} data-field="consent">
        <label>
          <input
            type="checkbox"
            checked={state.consent}
            onChange={(e) => patch({ consent: e.target.checked })}
          />
          <span>
            {CONSENT_TEXT}{' '}
            <a href={`${LEGAL}/privacy`} target="_blank" rel="noopener noreferrer">
              Privacy policy
            </a>
          </span>
        </label>
        {errors.consent ? (
          <p className="st-err" role="alert">
            {errors.consent}
          </p>
        ) : null}
      </div>
    </section>
  );
}

// ─── Thanks ──────────────────────────────────────────────────────────────

export interface ThanksInfo {
  ref: string;
  name: string;
  channel: Channel;
  category: Category;
}

export function Thanks({
  info,
  onMessenger,
  onAgain,
}: {
  info: ThanksInfo;
  onMessenger: () => void;
  onAgain: () => void;
}) {
  const first = info.name.trim().split(/\s+/)[0] ?? '';
  const viaMessenger = info.channel === 'messenger';
  return (
    <section className="st-thanks" aria-labelledby="st-thanks-h">
      <p className="st-eyebrow">Request sent</p>
      <h1 id="st-thanks-h">Got it{first ? `, ${first}` : ''}.</h1>
      <p className="st-dek">
        {viaMessenger
          ? 'Send us a message on Messenger so we can reply there. Mention your reference.'
          : `We'll reach you on ${labelOf(CHANNELS, info.channel)} as soon as we can.`}
      </p>

      <div className="st-ref">
        <span className="st-ref-k">Your reference</span>
        <span className="st-ref-v">{info.ref}</span>
      </div>

      <div className="st-thanks-actions">
        <a
          className={`st-btn ${viaMessenger ? 'st-btn-primary' : 'st-btn-ghost'} st-btn-wide`}
          href={MESSENGER_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onMessenger}
        >
          {viaMessenger ? 'Message us on Messenger' : 'Can’t wait? Message us on Messenger'}
        </a>
        <button type="button" className="st-link" onClick={onAgain}>
          Start another request
        </button>
      </div>
    </section>
  );
}

// ─── Below the fold ──────────────────────────────────────────────────────

export function BelowFold() {
  return (
    <div className="st-below">
      <section aria-labelledby="st-how">
        <p className="st-eyebrow">How it works</p>
        <h2 id="st-how" className="st-sec-h">
          Four steps, no brief to write.
        </h2>
        <ol className="st-how">
          {HOW.map(([t, b], i) => (
            <li key={t}>
              <span className="st-how-n">{String(i + 1).padStart(2, '0')}</span>
              <span className="st-how-t">{t}</span>
              <span className="st-how-b">{b}</span>
            </li>
          ))}
        </ol>
      </section>
      <section aria-labelledby="st-faq">
        <p className="st-eyebrow">Questions</p>
        <h2 id="st-faq" className="st-sec-h">
          Before you ask.
        </h2>
        <dl className="st-faq">
          {FAQ.map(([q, a]) => (
            <div key={q}>
              <dt>{q}</dt>
              <dd>{a}</dd>
            </div>
          ))}
        </dl>
        <p className="st-faq-more">
          <a href={LOGO_PAGE_URL}>Everything about logo work</a>
        </p>
      </section>
    </div>
  );
}

/** The same key the platform's notice uses, so one dismissal covers both. */
const COOKIE_KEY = 'codasc-cookie-notice';

/**
 * Cookie notice — a NOTICE, not an opt-in gate, matching the policy the rest
 * of Codasc runs on (platform/web/src/components/CookieNotice.tsx).
 *
 * It has to exist separately here because this page is its own SPA: the Next
 * root layout that renders the platform's copy never runs on /studio. And this
 * is the page that sets the most — GA's `_ga` plus Meta's `_fbp` and `_fbc` —
 * so being the one page without a notice was exactly backwards.
 *
 * Step 1 only. Steps 2 and 3 end in a sticky action bar pinned to the bottom
 * of a phone screen, and a banner parked over the Continue button would cost
 * leads. Arrival is when the cookies are set and when there is room to say so.
 */
export function CookieNotice({ show }: { show: boolean }) {
  // Starts hidden and is revealed by the effect: rendering it before
  // localStorage has been read would flash it at someone who dismissed it.
  const [seen, setSeen] = useState(true);

  useEffect(() => {
    try {
      setSeen(localStorage.getItem(COOKIE_KEY) !== null);
    } catch {
      // Blocked storage (private mode): we could not remember a dismissal, so
      // showing it every time would be nagging. Stay quiet.
    }
  }, []);

  if (seen || !show) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(COOKIE_KEY, 'dismissed');
    } catch {
      // Worst case it comes back next visit.
    }
    setSeen(true);
  };

  return (
    <div className="st-cookie" role="region" aria-label="Cookie notice">
      <p>
        We use cookies to see how this page is used and which ads bring people here. Read the{' '}
        <a href={`${LEGAL}/cookies`} target="_blank" rel="noopener noreferrer">
          Cookie Policy
        </a>
        .
      </p>
      <button type="button" className="st-btn st-btn-ghost st-cookie-ok" onClick={dismiss}>
        Got it
      </button>
    </div>
  );
}

export function Footer() {
  return (
    <footer className="st-footer">
      <nav aria-label="Legal">
        <a href={`${LEGAL}/privacy`} target="_blank" rel="noopener noreferrer">
          Privacy
        </a>
        <a href={`${LEGAL}/terms`} target="_blank" rel="noopener noreferrer">
          Terms
        </a>
        <a href={`${LEGAL}/cookies`} target="_blank" rel="noopener noreferrer">
          Cookies
        </a>
        <a href={SITE_URL} target="_blank" rel="noopener noreferrer">
          codasc.dev
        </a>
      </nav>
      <p>© 2026 CODASC SOFTWARE PUBLISHING</p>
    </footer>
  );
}
